import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, Clock, Award } from "lucide-react";
import api from "../lib/api";
import { EditableText } from "./live/Editable";

export default function CoursesSection() {
  const [courses, setCourses] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api
      .get("/courses", { params: { active_only: true } })
      .then((r) => setCourses(r.data))
      .catch(() => {});
  }, []);

  const filtered =
    filter === "all"
      ? courses
      : courses.filter((c) => c.category === filter);

  const tabs = [
    { key: "all", label: "All Courses" },
    { key: "long_term", label: "Long Term" },
    { key: "short_term", label: "Short Term" },
    { key: "corporate", label: "Corporate" },
    { key: "earn_while_learn", label: "Earn While You Learn" },
  ];

  return (
    <section className="relative py-24 bg-slate-50 overflow-hidden">
      <div className="absolute top-0 left-0 w-96 h-96 bg-yellow-100/40 rounded-full blur-3xl" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <EditableText path="home.courses.kicker" as="p" className="kicker mb-3">
              Hot topics. Awesome instructors.
            </EditableText>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B2A6B] leading-tight">
              <EditableText path="home.courses.heading" as="span">
                Check out our trending courses
              </EditableText>
            </h2>
          </div>
          <Link
            to="/courses"
            className="inline-flex items-center gap-2 text-[#0B2A6B] font-semibold hover:text-yellow-500 transition group"
          >
            Browse all courses
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition" />
          </Link>
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-10">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={`px-4 py-2 text-sm font-semibold rounded-full transition border ${
                filter === t.key
                  ? "bg-[#0B2A6B] text-white border-[#0B2A6B]"
                  : "bg-white text-slate-700 border-slate-200 hover:border-[#0B2A6B] hover:text-[#0B2A6B]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Course grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {filtered.slice(0, 9).map((c, i) => {
            const Icon = Icons[c.icon] || Icons.GraduationCap;
            return (
              <Link
                key={c.id}
                to={`/courses/${c.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-slate-100 tilt-card animate-fade-up"
                style={{ animationDelay: `${i * 0.06}s` }}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={c.image_url}
                    alt={c.title}
                    className="absolute inset-0 w-full h-full object-cover zoom-img"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  <div className="absolute top-4 left-4 flex gap-2">
                    <span className="px-3 py-1 rounded-full bg-yellow-400 text-[#0B2A6B] text-[11px] font-bold uppercase tracking-wider">
                      {c.category.replace("_", " ")}
                    </span>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4 flex justify-between items-center text-white text-xs font-medium">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      {c.duration}
                    </span>
                    {c.featured && (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-white/20 rounded-full backdrop-blur">
                        <Award className="h-3 w-3" />
                        Featured
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="h-10 w-10 shrink-0 rounded-lg bg-yellow-400/15 group-hover:bg-yellow-400 transition flex items-center justify-center">
                      <Icon className="h-5 w-5 text-yellow-600 group-hover:text-[#0B2A6B] transition" />
                    </div>
                    <h3 className="font-bold text-[#0B2A6B] text-lg leading-snug group-hover:text-yellow-600 transition">
                      {c.title}
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 line-clamp-3 leading-relaxed">
                    {c.description}
                  </p>
                  <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-xs text-slate-500">{c.eligibility}</span>
                    <span className="text-yellow-600 font-bold text-sm inline-flex items-center gap-1 group-hover:translate-x-1 transition">
                      Learn more <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16 text-slate-500">No courses available.</div>
        )}
      </div>
    </section>
  );
}
